import { PlusCircle, Search } from 'lucide-react';
import { useId, useState } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

export interface FilterOption {
  value: string;
  label: string;
}

export function TableFilter({
  title,
  options,
  value,
  onChange,
  disabled,
}: {
  title: string;
  options: FilterOption[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}) {
  const id = useId();
  const [search, setSearch] = useState('');
  const selected = new Set(value);
  const labels = value.map(
    (item) => options.find((option) => option.value === item)?.label ?? item,
  );
  const normalize = (text: string) => text.normalize('NFKC').toLowerCase();
  const filtered = options.filter((option) =>
    normalize(option.label).includes(normalize(search.trim())),
  );
  return (
    <Popover
      onOpenChange={(open) => {
        if (!open) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 border-dashed text-xs"
          aria-label={`筛选${title}`}
          aria-describedby={value.length ? `${id}-selection` : undefined}
          disabled={disabled && value.length === 0}
        >
          <PlusCircle className="size-3.5" />
          {title}
          {value.length > 0 && (
            <span
              id={`${id}-selection`}
              className="ml-1 flex min-w-0 items-center gap-1 border-l pl-2"
            >
              <span className="sr-only">已选</span>
              {labels.length > 2 ? (
                <Badge variant="secondary" className="px-1.5 text-[10px]">
                  {labels.length}
                </Badge>
              ) : (
                labels.map((label, index) => (
                  <Badge
                    key={value[index]}
                    variant="secondary"
                    className="max-w-24 truncate px-1.5 text-[10px]"
                    title={label}
                  >
                    {label}
                  </Badge>
                ))
              )}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-0" aria-label={`${title}筛选选项`}>
        {options.length > 6 && (
          <div className="relative border-b p-2">
            <Search className="absolute left-4 top-4 size-3.5 text-muted-foreground" />
            <Input
              aria-label={`搜索${title}选项`}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={`搜索${title}`}
              className="h-8 border-0 pl-7 shadow-none"
            />
          </div>
        )}
        <fieldset aria-label={title} className="max-h-64 overflow-y-auto p-1">
          {filtered.map((option) => (
            <Label
              key={option.value}
              htmlFor={`${id}-${option.value}`}
              className="min-h-9 cursor-pointer gap-2 rounded-sm px-2 py-2 text-xs hover:bg-accent has-[:focus-visible]:bg-accent"
            >
              <Checkbox
                id={`${id}-${option.value}`}
                disabled={disabled && !selected.has(option.value)}
                aria-label={option.label}
                checked={selected.has(option.value)}
                onCheckedChange={(checked) =>
                  onChange(
                    checked === true
                      ? [...value, option.value]
                      : value.filter((item) => item !== option.value),
                  )
                }
              />
              <span className="min-w-0 flex-1 truncate" title={option.label}>
                {option.label}
              </span>
            </Label>
          ))}
          {filtered.length === 0 && (
            <p className="py-5 text-center text-xs text-muted-foreground">无匹配选项</p>
          )}
        </fieldset>
        {value.length > 0 && (
          <div className="border-t p-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-full text-xs"
              onClick={() => onChange([])}
            >
              清除筛选
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
